"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useRouter } from "@/app/hooks/useRouter";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { Switch } from "antd";
import { TradeBoxInterface } from "@/app/components/TradeBox/TradeBox.interface";
import cn from "@/app/utils/cn";
import { handleLoginRedirect } from "@/app/utils/handleLoginRedirect";
import useRoundDown from "@/app/utils/formatAmount";
import { useFeatureStatus } from "@/app/context/FeatureStatusContext";
import { sendGTMEvent } from "@/app/utils/analytics";
import AmountInput from "@/app/components/Common/Input/AmountInput";
import IInput from "@/app/components/Common/Input";
import Button from "@/app/components/UI/Button";
import TradeModeTabs from "@/app/components/TradeBox/TradeModeTabs";
import CoinSVG from "../../../../public/Icons/CoinIcons/Coin";
import { CircleInfoIcon, ColorArrowDownIcon, PrimarySwapIcon } from "@/app/components/UI/Icons";
import { useCoinNameFromSymbol } from "@/app/utils/getCoinNameFromSymbol";
import isMobileView from "@/app/utils/isMobileView";
const SelectCryptoModal = dynamic(() => import("@/app/components/Common/ISelectCrypto/SelectCryptoModal"), {
  ssr: false
});
const QUICK_ORDER_CURRENCIES = ["IRT", "USDT"];
const RESTRICTED_COIN_SELECTOR_PATHS = ["/quick-order", "/limit-order"];
const TradeBox = t0 => {
  const $ = _c(308);
  const {
    isLogin,
    selectedFromMarket,
    setSelectedFromMarket,
    selectedToMarket,
    setSelectedToMarket,
    setIsMarketsModalOpen,
    isMarketsModalOpen,
    activeBox,
    markets,
    coinsState,
    setActiveBox,
    transformedMarkets,
    handleCommit,
    finalMarket,
    shownPrice,
    fromAmount,
    setFromAmount,
    toAmount,
    setToAmount,
    side,
    wallet,
    isLoading,
    isWalletLoading,
    baseSide,
    setBaseSide,
    chosenPrice,
    setChosenPrice,
    chosenPricePercentage,
    setChosenPricePercentage,
    isChartShown,
    setIsChartShown,
    priceSide,
    setPriceSide,
    maxOrderMultiplier: t1,
    prices
  } = t0;
  const maxOrderMultiplier = t1 === undefined ? 1 : t1;
  useTranslations();
  const router = useRouter();
  const path = usePathname();
  const {
    roundDown,
    roundDownNoFormat,
    priceRoundDown
  } = useRoundDown();
  const featureStatus = useFeatureStatus();
  const isExactEnabled = featureStatus.exact;
  const isStopLossEnabled = featureStatus.stopLoss;
  const getCoinName = useCoinNameFromSymbol();
  const quoteSymbol = finalMarket.split("-")[1];
  const defaultPricePlaceholder = priceRoundDown(shownPrice, quoteSymbol);
  const isDefaultChosenPrice = priceSide !== "value" && (chosenPricePercentage ?? 100) === 100;
  const displayedChosenPrice = isDefaultChosenPrice || chosenPrice === 0 ? null : chosenPrice ?? null;
  const [amountError, setAmountError] = useState(false);
  const [isFromAmountFocused, setIsFromAmountFocused] = useState(false);
  const [receiveSideValidationAmount, setReceiveSideValidationAmount] = useState(null);
  const [openedInfo, setOpenedInfo] = useState(false);
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const [marketSearchQuery, setMarketSearchQuery] = useState("");
  const isSubmitLockedRef = useRef(false);
  const submitLockTimerRef = useRef(null);
  const limitButtonArray = isStopLossEnabled ? [-10, -5, 5, 10] : [1, 5, 10];
  const walletFrom = isLogin && selectedFromMarket ? wallet[selectedFromMarket.from_symbol] ?? 0 : 0;
  const walletTo = isLogin && selectedToMarket ? wallet[selectedToMarket.name] ?? 0 : 0;
  const isWalletBalanceInsufficient = isLogin && !isWalletLoading && Number(fromAmount) > 0 && Number(fromAmount) > Number(walletFrom);
  const relativePricePercent = chosenPricePercentage ?? 100;
  const priceMovePercent = Math.abs(Math.round((relativePricePercent - 100) * 100) / 100);
  const isTakeProfit = side === "BUY" ? relativePricePercent <= 100 : relativePricePercent >= 100;
  const isStopLossMode = priceMovePercent > 0 && !isTakeProfit;
  const isStopLossBlocked = path === "/limit-order" && !isStopLossEnabled && isStopLossMode;
  const selectedMaxAmount = Number(selectedToMarket?.max_amount);
  let t2;
  if ($[0] !== maxOrderMultiplier || $[1] !== selectedMaxAmount) {
    t2 = Number.isFinite(selectedMaxAmount) ? selectedMaxAmount * maxOrderMultiplier : undefined;
    $[0] = maxOrderMultiplier;
    $[1] = selectedMaxAmount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const effectiveMaxAmount = t2;
  let t3;
  if ($[3] !== effectiveMaxAmount || $[4] !== selectedToMarket?.min_amount) {
    t3 = amount => !!amount && (amount < selectedToMarket?.min_amount || amount > (effectiveMaxAmount ?? Infinity));
    $[3] = effectiveMaxAmount;
    $[4] = selectedToMarket?.min_amount;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isAmountOutOfRange = t3;
  const validationAmount = baseSide === "to" ? receiveSideValidationAmount : fromAmount;
  const activePrice = path === "/limit-order" ? chosenPrice ?? shownPrice : shownPrice;
  let t4;
  if ($[6] !== activePrice || $[7] !== path) {
    t4 = path === "/limit-order" && (!Number.isFinite(activePrice) || activePrice <= 0);
    $[6] = activePrice;
    $[7] = path;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isActivePriceInvalid = t4;
  const isTradeFormInvalid = !fromAmount || !toAmount || !shownPrice || amountError || isWalletBalanceInsufficient || Number(walletFrom) == null || isSubmitLocked || isActivePriceInvalid || path === "/limit-order" && !isExactEnabled || isStopLossBlocked;
  let t5;
  if ($[9] !== path || $[10] !== setChosenPrice || $[11] !== setChosenPricePercentage || $[12] !== setFromAmount || $[13] !== setToAmount) {
    t5 = () => {
      setFromAmount(0);
      setToAmount(0);
      setReceiveSideValidationAmount(null);
      if (path === "/limit-order") {
        setChosenPricePercentage(100);
        setChosenPrice(null);
      }
    };
    $[9] = path;
    $[10] = setChosenPrice;
    $[11] = setChosenPricePercentage;
    $[12] = setFromAmount;
    $[13] = setToAmount;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== selectedFromMarket || $[16] !== selectedToMarket) {
    t6 = [selectedToMarket, selectedFromMarket];
    $[15] = selectedFromMarket;
    $[16] = selectedToMarket;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  if ($[18] !== isAmountOutOfRange || $[19] !== validationAmount) {
    t7 = () => {
      setAmountError(isAmountOutOfRange(validationAmount ?? 0));
    };
    $[18] = isAmountOutOfRange;
    $[19] = validationAmount;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = selectedToMarket?.min_amount;
  let t9;
  if ($[21] !== effectiveMaxAmount || $[22] !== t8 || $[23] !== validationAmount) {
    t9 = [effectiveMaxAmount, t8, validationAmount];
    $[21] = effectiveMaxAmount;
    $[22] = t8;
    $[23] = validationAmount;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  useEffect(t7, t9);
  let t10;
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => () => {
      if (submitLockTimerRef.current) {
        clearTimeout(submitLockTimerRef.current);
      }
    };
    t11 = [];
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  useEffect(t10, t11);
  let t12;
  if ($[27] !== baseSide || $[28] !== chosenPrice || $[29] !== chosenPricePercentage || $[30] !== finalMarket || $[31] !== fromAmount || $[32] !== path || $[33] !== priceSide || $[34] !== roundDownNoFormat || $[35] !== selectedFromMarket?.from_symbol || $[36] !== selectedToMarket?.name || $[37] !== setChosenPrice || $[38] !== setChosenPricePercentage || $[39] !== setFromAmount || $[40] !== setToAmount || $[41] !== shownPrice || $[42] !== side || $[43] !== toAmount) {
    t12 = () => {
      let price = path === "/limit-order" ? chosenPrice ?? shownPrice : shownPrice;
      if (path === "/limit-order") {
        if (priceSide === "value") {
          if (shownPrice > 0 && Number.isFinite(price)) {
            setChosenPricePercentage(Math.round(price * 10000 / shownPrice) / 100);
          }
        } else {
          if (shownPrice > 0) {
            price = roundDownNoFormat((chosenPricePercentage ?? 100) * shownPrice / 100, finalMarket.split("-")[1]);
            setChosenPrice(price);
          } else {
            price = 0;
            setChosenPrice(0);
          }
        }
      }
      if (!Number.isFinite(price) || price <= 0) {
        if (baseSide === "from") {
          setToAmount(0);
        } else {
          setFromAmount(0);
        }
        return;
      }
      if (baseSide === "from") {
        setReceiveSideValidationAmount(null);
        const newToAmount = roundDownNoFormat(side === "BUY" ? fromAmount / price : fromAmount * price, selectedToMarket?.name);
        if (!fromAmount) {
          setToAmount(0);
        } else {
          setToAmount(newToAmount);
        }
      } else {
        const newFromAmount = roundDownNoFormat(side === "BUY" ? toAmount * price : toAmount / price, selectedFromMarket?.from_symbol);
        setReceiveSideValidationAmount(toAmount ? newFromAmount : null);
        if (!toAmount) {
          setFromAmount(0);
        } else {
          setFromAmount(newFromAmount);
        }
      }
    };
    $[27] = baseSide;
    $[28] = chosenPrice;
    $[29] = chosenPricePercentage;
    $[30] = finalMarket;
    $[31] = fromAmount;
    $[32] = path;
    $[33] = priceSide;
    $[34] = roundDownNoFormat;
    $[35] = selectedFromMarket?.from_symbol;
    $[36] = selectedToMarket?.name;
    $[37] = setChosenPrice;
    $[38] = setChosenPricePercentage;
    $[39] = setFromAmount;
    $[40] = setToAmount;
    $[41] = shownPrice;
    $[42] = side;
    $[43] = toAmount;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const t13 = selectedFromMarket?.from_symbol;
  const t14 = selectedToMarket?.min_amount;
  const t15 = selectedToMarket?.name;
  let t16;
  if ($[45] !== baseSide || $[46] !== chosenPrice || $[47] !== chosenPricePercentage || $[48] !== effectiveMaxAmount || $[49] !== finalMarket || $[50] !== fromAmount || $[51] !== path || $[52] !== priceSide || $[53] !== shownPrice || $[54] !== side || $[55] !== t13 || $[56] !== t14 || $[57] !== t15 || $[58] !== toAmount) {
    t16 = [baseSide, chosenPrice, chosenPricePercentage, effectiveMaxAmount, finalMarket, fromAmount, path, priceSide, t13, t14, t15, shownPrice, side, toAmount];
    $[45] = baseSide;
    $[46] = chosenPrice;
    $[47] = chosenPricePercentage;
    $[48] = effectiveMaxAmount;
    $[49] = finalMarket;
    $[50] = fromAmount;
    $[51] = path;
    $[52] = priceSide;
    $[53] = shownPrice;
    $[54] = side;
    $[55] = t13;
    $[56] = t14;
    $[57] = t15;
    $[58] = toAmount;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  useEffect(t12, t16);
  let t17;
  if ($[60] !== coinsState) {
    t17 = symbol => {
      if (!symbol) {
        return 2;
      }
      const data = Array.isArray(coinsState) ? coinsState : [];
      const coin = data.find(c => c.symbol === symbol);
      return coin?.floating_point ?? 2;
    };
    $[60] = coinsState;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  const getFloatingPoint = t17;
  let t18;
  if ($[62] !== path) {
    t18 = RESTRICTED_COIN_SELECTOR_PATHS.includes(path);
    $[62] = path;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  const hasRestrictedCoinSelector = t18;
  const isRestrictedSelectorFromNonDefault = hasRestrictedCoinSelector && !QUICK_ORDER_CURRENCIES.includes(selectedFromMarket?.from_symbol);
  const isRestrictedSelectorToNonDefault = hasRestrictedCoinSelector && !QUICK_ORDER_CURRENCIES.includes(selectedToMarket?.name);
  selectedFromMarket?.from_symbol;
  let t19;
  bb0: {
    if (activeBox === "from") {
      if (isRestrictedSelectorToNonDefault) {
        let t20;
        if ($[64] !== path || $[65] !== transformedMarkets) {
          t20 = QUICK_ORDER_CURRENCIES.map(symbol_0 => {
            if (transformedMarkets?.[symbol_0]) {
              return transformedMarkets[symbol_0];
            }
            return path === "/quick-order" ? {
              from_symbol: symbol_0,
              to_symbols: []
            } : undefined;
          }).filter(Boolean);
          $[64] = path;
          $[65] = transformedMarkets;
          $[66] = t20;
        } else {
          t20 = $[66];
        }
        t19 = t20;
        break bb0;
      }
      let t20;
      if ($[67] !== markets) {
        t20 = markets ?? [];
        $[67] = markets;
        $[68] = t20;
      } else {
        t20 = $[68];
      }
      t19 = t20;
      break bb0;
    }
    const fromSymbol = selectedFromMarket?.from_symbol;
    let t20;
    if ($[69] !== fromSymbol || $[70] !== transformedMarkets) {
      t20 = fromSymbol ? transformedMarkets?.[fromSymbol]?.to_symbols ?? [] : [];
      $[69] = fromSymbol;
      $[70] = transformedMarkets;
      $[71] = t20;
    } else {
      t20 = $[71];
    }
    const toMarkets = t20;
    if (isRestrictedSelectorFromNonDefault) {
      let t21;
      if ($[72] !== path || $[73] !== toMarkets) {
        t21 = QUICK_ORDER_CURRENCIES.map(symbol_1 => {
          const market = toMarkets.find(item => item.name === symbol_1);
          if (market) {
            return market;
          }
          return path === "/quick-order" ? {
            name: symbol_1
          } : undefined;
        }).filter(Boolean);
        $[72] = path;
        $[73] = toMarkets;
        $[74] = t21;
      } else {
        t21 = $[74];
      }
      t19 = t21;
      break bb0;
    }
    if (hasRestrictedCoinSelector) {
      let t21;
      if ($[75] !== markets) {
        t21 = (markets ?? []).reduce(_temp, []);
        $[75] = markets;
        $[76] = t21;
      } else {
        t21 = $[76];
      }
      t19 = t21;
      break bb0;
    }
    t19 = toMarkets;
  }
  const activeMarkets = t19;
  let t20;
  if ($[77] !== handleCommit || $[78] !== isLogin || $[79] !== isTradeFormInvalid || $[80] !== path || $[81] !== router || $[82] !== setPriceSide || $[83] !== side) {
    t20 = () => {
      if (isLogin) {
        if (isTradeFormInvalid || isSubmitLockedRef.current) {
          return;
        }
        isSubmitLockedRef.current = true;
        setIsSubmitLocked(true);
        if (submitLockTimerRef.current) {
          clearTimeout(submitLockTimerRef.current);
        }
        submitLockTimerRef.current = setTimeout(() => {
          isSubmitLockedRef.current = false;
          setIsSubmitLocked(false);
          submitLockTimerRef.current = null;
        }, 1200);
        if (path === "/limit-order") {
          setPriceSide("value");
        }
        handleCommit();
        sendGTMEvent({
          event: `comp-${side}`,
          page: path.replace("/", ""),
          action: "trigger"
        });
      } else {
        handleLoginRedirect({
          router
        });
      }
    };
    $[77] = handleCommit;
    $[78] = isLogin;
    $[79] = isTradeFormInvalid;
    $[80] = path;
    $[81] = router;
    $[82] = setPriceSide;
    $[83] = side;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  const handleLoginButton = t20;
  let t21;
  if ($[85] !== path || $[86] !== selectedFromMarket?.from_symbol || $[87] !== selectedToMarket?.name || $[88] !== setChosenPrice || $[89] !== setChosenPricePercentage || $[90] !== setSelectedFromMarket || $[91] !== setSelectedToMarket || $[92] !== transformedMarkets) {
    t21 = e => {
      e.preventDefault();
      const newFromMarket = transformedMarkets[selectedToMarket?.name];
      if (!newFromMarket) {
        return console.error("newFromMarket is undefined");
      }
      const newToMarket = newFromMarket.to_symbols.find(m => m.name === selectedFromMarket?.from_symbol);
      if (path === "/limit-order") {
        setChosenPrice(null);
        setChosenPricePercentage(100);
      }
      setSelectedFromMarket(newFromMarket);
      setSelectedToMarket(newToMarket);
    };
    $[85] = path;
    $[86] = selectedFromMarket?.from_symbol;
    $[87] = selectedToMarket?.name;
    $[88] = setChosenPrice;
    $[89] = setChosenPricePercentage;
    $[90] = setSelectedFromMarket;
    $[91] = setSelectedToMarket;
    $[92] = transformedMarkets;
    $[93] = t21;
  } else {
    t21 = $[93];
  }
  const handleSwap = t21;
  let t22;
  if ($[94] !== setActiveBox || $[95] !== setIsMarketsModalOpen || $[96] !== setMarketSearchQuery) {
    t22 = (e_0, type) => {
      e_0.preventDefault();
      setActiveBox(type);
      setMarketSearchQuery("");
      setIsMarketsModalOpen(true);
    };
    $[94] = setActiveBox;
    $[95] = setIsMarketsModalOpen;
    $[96] = setMarketSearchQuery;
    $[97] = t22;
  } else {
    t22 = $[97];
  }
  const showModal = t22;
  let t23;
  if ($[98] !== activeBox || $[99] !== selectedFromMarket?.from_symbol || $[100] !== selectedToMarket?.name || $[101] !== setIsMarketsModalOpen || $[102] !== setMarketSearchQuery || $[103] !== setSelectedFromMarket || $[104] !== setSelectedToMarket || $[105] !== transformedMarkets) {
    t23 = selection => {
      const fromSymbol_0 = selection?.from_symbol ?? selection?.symbol;
      const toSymbol = selection?.name ?? selection?.symbol;
      if (activeBox === "from") {
        if (!fromSymbol_0) {
          return;
        }
        const nextFromMarket = transformedMarkets?.[fromSymbol_0];
        if (!nextFromMarket) {
          return;
        }
        setSelectedFromMarket(nextFromMarket);
        const currentToSymbol = selectedToMarket?.name;
        const nextToMarket = fromSymbol_0 === currentToSymbol ? nextFromMarket.to_symbols[0] : nextFromMarket.to_symbols.find(market_1 => market_1.name === currentToSymbol) ?? nextFromMarket.to_symbols[0];
        setSelectedToMarket(nextToMarket);
        setMarketSearchQuery("");
        setIsMarketsModalOpen(false);
        return;
      }
      if (!toSymbol) {
        return;
      }
      if (toSymbol === selectedFromMarket?.from_symbol) {
        const nextFromMarket_0 = transformedMarkets?.[toSymbol];
        const nextFromSymbol = nextFromMarket_0?.to_symbols?.[0]?.name;
        const replacementFromMarket = nextFromSymbol ? transformedMarkets?.[nextFromSymbol] : undefined;
        const replacementToMarket = replacementFromMarket?.to_symbols?.find(market_2 => market_2.name === toSymbol);
        if (!replacementFromMarket || !replacementToMarket) {
          return;
        }
        setSelectedFromMarket(replacementFromMarket);
        setSelectedToMarket(replacementToMarket);
        setMarketSearchQuery("");
        setIsMarketsModalOpen(false);
        return;
      }
      const nextToMarket_0 = transformedMarkets?.[selectedFromMarket?.from_symbol]?.to_symbols.find(market_3 => market_3.name === toSymbol);
      if (!nextToMarket_0) {
        const reverseFromMarket = transformedMarkets?.[toSymbol];
        const reverseToMarket = reverseFromMarket?.to_symbols?.find(market_4 => market_4.name === selectedFromMarket?.from_symbol);
        if (!reverseFromMarket || !reverseToMarket) {
          return;
        }
        setSelectedFromMarket(reverseFromMarket);
        setSelectedToMarket(reverseToMarket);
        setMarketSearchQuery("");
        setIsMarketsModalOpen(false);
        return;
      }
      setSelectedToMarket(nextToMarket_0);
      setMarketSearchQuery("");
      setIsMarketsModalOpen(false);
    };
    $[98] = activeBox;
    $[99] = selectedFromMarket?.from_symbol;
    $[100] = selectedToMarket?.name;
    $[101] = setIsMarketsModalOpen;
    $[102] = setMarketSearchQuery;
    $[103] = setSelectedFromMarket;
    $[104] = setSelectedToMarket;
    $[105] = transformedMarkets;
    $[106] = t23;
  } else {
    t23 = $[106];
  }
  const handleModalSelect = t23;
  let t24;
  if ($[107] !== activeBox) {
    t24 = market_5 => activeBox === "from" ? market_5?.from_symbol : market_5?.name;
    $[107] = activeBox;
    $[108] = t24;
  } else {
    t24 = $[108];
  }
  const getMarketOptionSymbol = t24;
  let t25;
  if ($[109] !== getCoinName || $[110] !== getMarketOptionSymbol) {
    t25 = market_6 => getCoinName(getMarketOptionSymbol(market_6));
    $[109] = getCoinName;
    $[110] = getMarketOptionSymbol;
    $[111] = t25;
  } else {
    t25 = $[111];
  }
  const getMarketOptionDisplayName = t25;
  let t26;
  if ($[112] !== getMarketOptionSymbol || $[113] !== roundDown || $[114] !== wallet) {
    t26 = market_7 => {
      const symbol_2 = getMarketOptionSymbol(market_7);
      return <>{roundDown(Number(wallet?.[symbol_2] || 0), symbol_2)}<span className="ps-1 text-sm font-semibold text-charcoal-900 md:text-base">{symbol_2}</span></>;
    };
    $[112] = getMarketOptionSymbol;
    $[113] = roundDown;
    $[114] = wallet;
    $[115] = t26;
  } else {
    t26 = $[115];
  }
  const getMarketPrimaryBalance = t26;
  let t27;
  if ($[116] !== getMarketOptionSymbol || $[117] !== prices || $[118] !== roundDown || $[119] !== wallet) {
    t27 = market_8 => {
      const symbol_3 = getMarketOptionSymbol(market_8);
      const balance = Number(wallet?.[symbol_3] || 0);
      const priceInIrt = Number(prices?.[symbol_3]?.price_in_irt || 0);
      return `~ ${roundDown(balance * priceInIrt, "IRT", true)} IRT`;
    };
    $[116] = getMarketOptionSymbol;
    $[117] = prices;
    $[118] = roundDown;
    $[119] = wallet;
    $[120] = t27;
  } else {
    t27 = $[120];
  }
  const getMarketSecondaryBalance = t27;
  let t28;
  if ($[121] !== effectiveMaxAmount || $[122] !== roundDown || $[123] !== roundDownNoFormat || $[124] !== selectedFromMarket?.from_symbol || $[125] !== setBaseSide || $[126] !== setFromAmount || $[127] !== walletFrom) {
    t28 = () => {
      setBaseSide("from");
      const max_amount = Math.min(Number(roundDown(walletFrom, selectedFromMarket?.from_symbol).replace(/,/g, "")), effectiveMaxAmount ?? Infinity);
      setFromAmount(roundDownNoFormat(max_amount, selectedFromMarket?.from_symbol));
    };
    $[121] = effectiveMaxAmount;
    $[122] = roundDown;
    $[123] = roundDownNoFormat;
    $[124] = selectedFromMarket?.from_symbol;
    $[125] = setBaseSide;
    $[126] = setFromAmount;
    $[127] = walletFrom;
    $[128] = t28;
  } else {
    t28 = $[128];
  }
  const handleMaxFromAmount = t28;
  let t29;
  if ($[129] !== setBaseSide || $[130] !== setFromAmount) {
    t29 = value => {
      setBaseSide("from");
      setReceiveSideValidationAmount(null);
      setFromAmount(value);
    };
    $[129] = setBaseSide;
    $[130] = setFromAmount;
    $[131] = t29;
  } else {
    t29 = $[131];
  }
  const handleFromAmountChange = t29;
  let t30;
  if ($[132] !== setBaseSide || $[133] !== setToAmount) {
    t30 = value_0 => {
      setBaseSide("to");
      setToAmount(value_0);
    };
    $[132] = setBaseSide;
    $[133] = setToAmount;
    $[134] = t30;
  } else {
    t30 = $[134];
  }
  const handleToAmountChange = t30;
  let t31;
  if ($[135] !== setChosenPrice || $[136] !== setPriceSide) {
    t31 = value_1 => {
      setPriceSide("value");
      setChosenPrice(value_1);
    };
    $[135] = setChosenPrice;
    $[136] = setPriceSide;
    $[137] = t31;
  } else {
    t31 = $[137];
  }
  const handleChosenPriceChange = t31;
  const changeChosenPricePercentage = (e_1, num, add) => {
    e_1.preventDefault();
    setPriceSide("percentage");
    const newPercentage = num + (add ? chosenPricePercentage : 100);
    setChosenPricePercentage(newPercentage);
    if (shownPrice) {
      setChosenPrice(roundDownNoFormat(newPercentage * shownPrice / 100, finalMarket.split("-")[1]));
    }
  };
  let t32;
  if ($[138] !== openedInfo) {
    t32 = e_2 => {
      e_2.preventDefault();
      setOpenedInfo(!openedInfo);
    };
    $[138] = openedInfo;
    $[139] = t32;
  } else {
    t32 = $[139];
  }
  const toggleInfo = t32;
  let t33;
  if ($[140] !== handleLoginButton) {
    t33 = e_3 => {
      if (e_3.key === "Enter") {
        e_3.preventDefault();
        handleLoginButton();
      }
    };
    $[140] = handleLoginButton;
    $[141] = t33;
  } else {
    t33 = $[141];
  }
  const handleKeyDown = t33;
  let t34;
  if ($[142] !== path) {
    t34 = <div className="md:hidden"><TradeModeTabs path={path} /></div>;
    $[142] = path;
    $[143] = t34;
  } else {
    t34 = $[143];
  }
  let t35;
  if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = cn("bg-white p-4 md:rounded-3xl md:border md:border-charcoal-300 md:px-6 md:pb-6 md:pt-4");
    $[144] = t35;
  } else {
    t35 = $[144];
  }
  let t36;
  if ($[145] !== path) {
    t36 = <div className="w-full"><TradeModeTabs path={path} /></div>;
    $[145] = path;
    $[146] = t36;
  } else {
    t36 = $[146];
  }
  let t37;
  if ($[147] !== isChartShown || $[148] !== path || $[149] !== setIsChartShown) {
    t37 = path === "/limit-order" && <div className="mt-3 flex h-14 w-full items-center justify-between border-b border-charcoal-200 md:mt-0 md:w-fit" dir="ltr"><Switch checked={isChartShown} onChange={setIsChartShown} /><span className="mx-2 text-nowrap text-sm font-medium text-charcoal-900">نمایش نمودار</span></div>;
    $[147] = isChartShown;
    $[148] = path;
    $[149] = setIsChartShown;
    $[150] = t37;
  } else {
    t37 = $[150];
  }
  let t38;
  if ($[151] !== t36 || $[152] !== t37) {
    t38 = <div className="mb-6 hidden items-center justify-between md:flex">{t36}{t37}</div>;
    $[151] = t36;
    $[152] = t37;
    $[153] = t38;
  } else {
    t38 = $[153];
  }
  const t39 = amountError || isWalletBalanceInsufficient ? "border-rojo-400" : isFromAmountFocused ? "border-bluetiful-400" : "border-charcoal-50";
  let t40;
  if ($[154] !== t39) {
    t40 = cn("flex flex-col rounded-2xl border bg-charcoal-50 p-4 md:p-6", t39);
    $[154] = t39;
    $[155] = t40;
  } else {
    t40 = $[155];
  }
  let t41;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <p className="text-xs text-charcoal-600 md:text-base md:font-medium">پرداخت می‌کنم</p>;
    $[156] = t41;
  } else {
    t41 = $[156];
  }
  let t42;
  if ($[157] !== handleMaxFromAmount || $[158] !== isLoading || $[159] !== isLogin || $[160] !== isWalletLoading || $[161] !== roundDown || $[162] !== selectedFromMarket || $[163] !== walletFrom) {
    t42 = isLogin && selectedFromMarket && <div className="flex items-center justify-end"><p className="px-1 text-xs font-medium text-charcoal-900 md:text-xs">موجودی:</p>{isWalletLoading || isLoading ? <div className="h-5 w-20 rounded-lg bg-gradient-to-r from-charcoal-300 to-charcoal-300" /> : <button type="button" onClick={handleMaxFromAmount} className="text-xs font-semibold text-bluetiful-400 underline md:text-xs" dir="ltr"><span>{roundDown(walletFrom, selectedFromMarket?.from_symbol)}{" "}</span><span>{selectedFromMarket.from_symbol}</span></button>}</div>;
    $[157] = handleMaxFromAmount;
    $[158] = isLoading;
    $[159] = isLogin;
    $[160] = isWalletLoading;
    $[161] = roundDown;
    $[162] = selectedFromMarket;
    $[163] = walletFrom;
    $[164] = t42;
  } else {
    t42 = $[164];
  }
  let t43;
  if ($[165] !== t42) {
    t43 = <div className="flex w-full justify-between">{t41}{t42}</div>;
    $[165] = t42;
    $[166] = t43;
  } else {
    t43 = $[166];
  }
  let t44;
  if ($[167] !== getCoinName || $[168] !== isLoading || $[169] !== selectedFromMarket?.from_symbol || $[170] !== showModal) {
    t44 = <div className="w-full max-w-fit"><div>{isLoading ? <div className="h-8 w-28 rounded-full bg-gradient-to-r from-charcoal-300 to-charcoal-300" /> : <button type="button" onClick={e_4 => showModal(e_4, "from")} className="flex h-9 items-center justify-center gap-3 rounded-[10px] bg-white px-2 py-1 md:h-[42px] md:px-3 md:py-3"><div className="flex items-center gap-1 md:gap-2"><CoinSVG id={selectedFromMarket?.from_symbol} className="h-4 w-4 md:h-5 md:w-5" /><span className="text-nowrap text-sm font-semibold text-charcoal-900 md:text-sm">{getCoinName(selectedFromMarket?.from_symbol)}</span></div><ColorArrowDownIcon className="h-5 w-5" color="#0C0D11" /></button>}</div></div>;
    $[167] = getCoinName;
    $[168] = isLoading;
    $[169] = selectedFromMarket?.from_symbol;
    $[170] = showModal;
    $[171] = t44;
  } else {
    t44 = $[171];
  }
  let t45;
  if ($[172] !== baseSide || $[173] !== effectiveMaxAmount || $[174] !== fromAmount || $[175] !== getFloatingPoint || $[176] !== handleFromAmountChange || $[177] !== handleKeyDown || $[178] !== isLoading || $[179] !== roundDown || $[180] !== selectedFromMarket?.from_symbol || $[181] !== selectedToMarket?.max_amount || $[182] !== selectedToMarket?.min_amount) {
    t45 = isLoading ? <div className="h-8 w-32 rounded-lg bg-gradient-to-r from-charcoal-300 to-charcoal-300" /> : <AmountInput value={fromAmount === 0 && baseSide !== "from" ? null : fromAmount ?? null} onChange={handleFromAmountChange} onKeyDown={handleKeyDown} onFocus={() => setIsFromAmountFocused(true)} onBlur={() => setIsFromAmountFocused(false)} type={selectedFromMarket?.from_symbol === "IRT" ? "fiat" : "crypto"} placeholder={`از ${roundDown(selectedToMarket?.min_amount, selectedFromMarket?.from_symbol)} تا ${roundDown(effectiveMaxAmount ?? selectedToMarket?.max_amount, selectedFromMarket?.from_symbol)}`} decimalLimit={selectedFromMarket?.from_symbol == "IRT" ? 0 : getFloatingPoint(selectedFromMarket?.from_symbol)} inputStyle="borderLess" customInputClass="!px-0 !py-0 !mt-0" customFieldClassName="text-start !px-0 !py-0 placeholder:text-charcoal-400 placeholder:text-sm md:placeholder:text-lg md:placeholder:font-medium !text-charcoal-900 text-base font-semibold" selectedCoin={selectedFromMarket?.from_symbol ? {
      symbol: selectedFromMarket?.from_symbol
    } : undefined} />;
    $[172] = baseSide;
    $[173] = effectiveMaxAmount;
    $[174] = fromAmount;
    $[175] = getFloatingPoint;
    $[176] = handleFromAmountChange;
    $[177] = handleKeyDown;
    $[178] = isLoading;
    $[179] = roundDown;
    $[180] = selectedFromMarket?.from_symbol;
    $[181] = selectedToMarket?.max_amount;
    $[182] = selectedToMarket?.min_amount;
    $[183] = t45;
  } else {
    t45 = $[183];
  }
  let t46;
  if ($[184] !== amountError || $[185] !== effectiveMaxAmount || $[186] !== isWalletBalanceInsufficient || $[187] !== roundDown || $[188] !== selectedFromMarket || $[189] !== selectedToMarket?.max_amount || $[190] !== selectedToMarket?.min_amount || $[191] !== validationAmount) {
    t46 = isWalletBalanceInsufficient ? <p className="mt-2 flex w-full items-center justify-end text-nowrap text-end text-[11px] font-medium text-rojo-400 md:text-xs">مقدار وارد شده از موجودی شما بیشتر است.</p> : amountError ? validationAmount && Number(validationAmount) < Number(selectedToMarket?.min_amount) ? <p className="mt-2 flex w-full items-center justify-end text-nowrap text-end text-[11px] font-medium text-rojo-400 md:text-xs">حداقل مقدار معامله<span className="mx-0.5 md:mx-1" dir="ltr"><span className="">{roundDown(selectedToMarket?.min_amount, selectedFromMarket.from_symbol)}</span>{" "}<span className="">{selectedFromMarket.from_symbol}</span></span>می‌باشد.</p> : <p className="mt-2 flex w-full items-center justify-end text-nowrap text-end text-[10px] font-medium text-rojo-400 md:text-xs">حداکثر مقدار معامله<span className="">{selectedFromMarket.from_symbol}{" "}</span><span className="mx-1">{roundDown(effectiveMaxAmount ?? selectedToMarket?.max_amount, selectedFromMarket.from_symbol)}</span>می‌باشد.</p> : null;
    $[184] = amountError;
    $[185] = effectiveMaxAmount;
    $[186] = isWalletBalanceInsufficient;
    $[187] = roundDown;
    $[188] = selectedFromMarket;
    $[189] = selectedToMarket?.max_amount;
    $[190] = selectedToMarket?.min_amount;
    $[191] = validationAmount;
    $[192] = t46;
  } else {
    t46 = $[192];
  }
  let t47;
  if ($[193] !== t45 || $[194] !== t46) {
    t47 = <div className="relative w-full">{t45}{t46}</div>;
    $[193] = t45;
    $[194] = t46;
    $[195] = t47;
  } else {
    t47 = $[195];
  }
  let t48;
  if ($[196] !== t44 || $[197] !== t47) {
    t48 = <div className="flex h-fit w-full items-center justify-between py-3">{t44}{t47}</div>;
    $[196] = t44;
    $[197] = t47;
    $[198] = t48;
  } else {
    t48 = $[198];
  }
  let t49;
  if ($[199] !== t40 || $[200] !== t43 || $[201] !== t48) {
    t49 = <div className={t40}>{t43}{t48}</div>;
    $[199] = t40;
    $[200] = t43;
    $[201] = t48;
    $[202] = t49;
  } else {
    t49 = $[202];
  }
  let t50;
  if ($[203] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <div><PrimarySwapIcon className="h-5 w-5 md:h-7 md:w-7" /></div>;
    $[203] = t50;
  } else {
    t50 = $[203];
  }
  let t51;
  if ($[204] !== handleSwap) {
    t51 = <div className="absolute left-[45%] right-[45%] top-[41%] md:top-[42%] xl:top-[40%]"><button type="button" onClick={handleSwap} className="flex h-[42px] w-[42px] items-center justify-center rounded-2xl border-4 border-white bg-charcoal-50 md:h-14 md:w-14">{t50}</button></div>;
    $[204] = handleSwap;
    $[205] = t51;
  } else {
    t51 = $[205];
  }
  let t52;
  if ($[206] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <p className="text-xs text-charcoal-600 md:text-base md:font-medium">دریافت می‌کنم</p>;
    $[206] = t52;
  } else {
    t52 = $[206];
  }
  let t53;
  if ($[207] !== toggleInfo) {
    t53 = e_5 => toggleInfo(e_5);
    $[207] = toggleInfo;
    $[208] = t53;
  } else {
    t53 = $[208];
  }
  let t54;
  if ($[209] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <CircleInfoIcon className="h-5 w-5 md:h-6 md:w-6" />;
    $[209] = t54;
  } else {
    t54 = $[209];
  }
  let t55;
  if ($[210] !== t53) {
    t55 = <div className="cursor-pointer" onClick={t53}>{t54}</div>;
    $[210] = t53;
    $[211] = t55;
  } else {
    t55 = $[211];
  }
  let t56;
  if ($[212] !== openedInfo) {
    t56 = openedInfo && <div className="absolute left-1/2 top-[calc(100%+10px)] z-20 w-[calc(100vw-10rem)] max-w-64 -translate-x-1/2 md:w-80"><div className="absolute -top-1.5 left-1/2 -translate-x-1/2"><div className="h-3.5 w-3.5 rotate-[135deg] rounded-bl border-b border-l border-charcoal-300 bg-white" /></div><div className="rounded-xl border border-charcoal-300 bg-white p-3"><p className="text-sm text-charcoal-900">مقدار نمایش داده شده، قبل از کسر کارمزد می‌باشد.</p></div></div>;
    $[212] = openedInfo;
    $[213] = t56;
  } else {
    t56 = $[213];
  }
  let t57;
  if ($[214] !== t55 || $[215] !== t56) {
    t57 = <div className="flex items-center gap-x-2">{t52}<div className="relative w-fit">{t55}{t56}</div></div>;
    $[214] = t55;
    $[215] = t56;
    $[216] = t57;
  } else {
    t57 = $[216];
  }
  let t58;
  if ($[217] !== isLoading || $[218] !== isLogin || $[219] !== isWalletLoading || $[220] !== roundDown || $[221] !== selectedToMarket || $[222] !== walletTo) {
    t58 = isLogin && selectedToMarket && <div className="flex items-center justify-end"><p className="px-1 text-xs font-medium text-charcoal-900 md:text-xs">موجودی:</p>{isWalletLoading || isLoading ? <div className="h-5 w-20 rounded-lg bg-gradient-to-r from-charcoal-300 to-charcoal-300" /> : <p className="text-xs font-semibold text-charcoal-600" dir="ltr"><span>{roundDown(walletTo, selectedToMarket?.name)}{" "}</span><span>{selectedToMarket?.name}</span></p>}</div>;
    $[217] = isLoading;
    $[218] = isLogin;
    $[219] = isWalletLoading;
    $[220] = roundDown;
    $[221] = selectedToMarket;
    $[222] = walletTo;
    $[223] = t58;
  } else {
    t58 = $[223];
  }
  let t59;
  if ($[224] !== t57 || $[225] !== t58) {
    t59 = <div className="flex w-full items-center justify-between">{t57}{t58}</div>;
    $[224] = t57;
    $[225] = t58;
    $[226] = t59;
  } else {
    t59 = $[226];
  }
  let t60;
  if ($[227] !== getCoinName || $[228] !== isLoading || $[229] !== selectedToMarket || $[230] !== showModal) {
    t60 = <div className="w-full max-w-fit"><div>{isLoading ? <div className="h-8 w-28 rounded-[10px] bg-gradient-to-r from-charcoal-300 to-charcoal-300" /> : <button type="button" onClick={e_6 => showModal(e_6, "to")} className={cn("flex h-9 items-center justify-center gap-3 rounded-[10px] px-2 py-1 md:h-[42px] md:px-3 md:py-3", selectedToMarket ? "bg-charcoal-50 text-charcoal-900" : "bg-bluetiful-400 text-white")}><div className="flex items-center gap-1 md:gap-2">{selectedToMarket && <CoinSVG id={selectedToMarket?.name} className="h-4 w-4 md:h-5 md:w-5" />}<span className="text-nowrap text-sm font-bold text-charcoal-900 md:text-sm">{selectedToMarket ? getCoinName(selectedToMarket?.name) : "\u0627\u0646\u062A\u062E\u0627\u0628 \u0627\u0631\u0632"}</span></div><ColorArrowDownIcon className="h-5 w-5" color={selectedToMarket ? "#0C0D11" : "#FFFFFF"} /></button>}</div></div>;
    $[227] = getCoinName;
    $[228] = isLoading;
    $[229] = selectedToMarket;
    $[230] = showModal;
    $[231] = t60;
  } else {
    t60 = $[231];
  }
  const t61 = toAmount === 0 && baseSide !== "to" ? null : toAmount ?? null;
  const t62 = selectedToMarket?.name === "IRT" ? "fiat" : "crypto";
  let t63;
  if ($[232] !== getFloatingPoint || $[233] !== selectedToMarket?.name) {
    t63 = selectedToMarket?.name === "IRT" ? 0 : getFloatingPoint(selectedToMarket?.name);
    $[232] = getFloatingPoint;
    $[233] = selectedToMarket?.name;
    $[234] = t63;
  } else {
    t63 = $[234];
  }
  let t64;
  if ($[235] !== selectedToMarket?.name) {
    t64 = selectedToMarket?.name ? {
      symbol: selectedToMarket?.name
    } : undefined;
    $[235] = selectedToMarket?.name;
    $[236] = t64;
  } else {
    t64 = $[236];
  }
  let t65;
  if ($[237] !== handleKeyDown || $[238] !== handleToAmountChange || $[239] !== t61 || $[240] !== t62 || $[241] !== t63 || $[242] !== t64) {
    t65 = <div className="relative w-full"><AmountInput value={t61} onChange={handleToAmountChange} onKeyDown={handleKeyDown} type={t62} placeholder="0" decimalLimit={t63} inputStyle="borderLess" customInputClass="!px-0 !py-0 !mt-0" customFieldClassName="text-start !px-0 !py-0 placeholder:text-charcoal-400 placeholder:text-sm md:placeholder:text-lg md:placeholder:font-medium !text-charcoal-900 text-base font-semibold" selectedCoin={t64} /></div>;
    $[237] = handleKeyDown;
    $[238] = handleToAmountChange;
    $[239] = t61;
    $[240] = t62;
    $[241] = t63;
    $[242] = t64;
    $[243] = t65;
  } else {
    t65 = $[243];
  }
  let t66;
  if ($[244] !== t60 || $[245] !== t65) {
    t66 = <div className="flex w-full items-center justify-between py-3">{t60}{t65}</div>;
    $[244] = t60;
    $[245] = t65;
    $[246] = t66;
  } else {
    t66 = $[246];
  }
  let t67;
  if ($[247] !== t59 || $[248] !== t66) {
    t67 = <div className="mt-1 rounded-2xl border-[1.4px] border-charcoal-200 p-4 md:mt-2 md:p-6">{t59}{t66}</div>;
    $[247] = t59;
    $[248] = t66;
    $[249] = t67;
  } else {
    t67 = $[249];
  }
  let t68;
  if ($[250] !== t49 || $[251] !== t51 || $[252] !== t67) {
    t68 = <div className="relative">{t49}{t51}{t67}</div>;
    $[250] = t49;
    $[251] = t51;
    $[252] = t67;
    $[253] = t68;
  } else {
    t68 = $[253];
  }
  const t69 = path === "/limit-order" && <><div className="mt-4 flex items-end justify-between gap-x-3"><div className="w-full flex-1 items-center justify-between rounded-2xl border-[1.4px] border-charcoal-200 p-4 md:p-6"><div className="flex items-center justify-between"><p className="text-xs text-charcoal-600 md:text-base md:font-semibold">قیمت فعال‌سازی</p><p className="cursor-pointer text-xs font-semibold text-bluetiful-400 md:hidden" onClick={() => setIsChartShown(true)}>مشاهده نمودار</p></div><div className="mt-3 w-full items-center justify-between md:flex" dir="ltr"><div className="" dir="ltr"><AmountInput value={displayedChosenPrice} onChange={handleChosenPriceChange} onKeyDown={handleKeyDown} type={quoteSymbol === "IRT" ? "fiat" : "crypto"} placeholder={defaultPricePlaceholder} decimalLimit={getFloatingPoint(quoteSymbol)} inputStyle="borderLess" customInputClass="relative inline-flex !w-fit !justify-start !px-0 !py-0 !mt-0" customFieldClassName="!w-fit !flex-none [field-sizing:content] text-start !px-0 !py-0 !mt-0 border-0 placeholder:text-charcoal-400 placeholder:text-sm md:placeholder:text-lg md:placeholder:font-medium !text-charcoal-900 text-base font-semibold" selectedCoin={{
              symbol: quoteSymbol
            }} suffix={quoteSymbol} suffixClass={cn("pointer-events-none ms-1 text-nowrap text-sm font-medium md:text-lg", displayedChosenPrice === null ? "text-charcoal-400" : "text-charcoal-900")} render={_temp2} /></div><div className="mt-2 flex w-full items-center justify-between gap-x-2 md:mb-0 md:mt-0 md:w-1/2"><div className="flex w-full items-center gap-x-2" dir="rtl">{limitButtonArray.map((val, _) => {
                const delta = isStopLossEnabled ? val : (side == "BUY" ? -1 : 1) * val;
                const isActive = delta + 100 == chosenPricePercentage;
                const absDelta = String(Math.abs(delta));
                return <Button key={val} variant="soft" color="neutral" size="xs" className={cn("", isActive ? "border-2 border-bluetiful-400" : "")} onClick={e_7 => changeChosenPricePercentage(e_7, delta, false)}><p className="inline-flex w-full text-nowrap leading-tight"><span>{absDelta}٪</span>  <span>{delta < 0 ? "\u067E\u0627\u06CC\u06CC\u0646\u200C\u062A\u0631" : "\u0628\u0627\u0644\u0627\u062A\u0631"}</span></p></Button>;
              })}</div></div></div></div></div>{isStopLossEnabled && priceMovePercent > 0 && <div className={cn("mt-2 flex w-full items-start justify-start gap-x-1.5 rounded-2xl p-2.5 text-xs font-medium", isTakeProfit ? "bg-aquamarine-50 text-charcoal-900" : "bg-crayola-50 text-charcoal-900")}><CircleInfoIcon className="h-4 w-4" /><span><span className="font-bold">{isTakeProfit ? "\u062D\u062F \u0633\u0648\u062F:" : "\u062D\u062F \u0636\u0631\u0631:"}</span><span>{" "}{isTakeProfit ? `با رسیدن قیمت به این سطح، سفارش شما برای ذخیره ${priceMovePercent}% سود فعال می‌شود.` : `با رسیدن قیمت به این سطح، سفارش شما برای محدود کردن ${priceMovePercent}% زیان فعال می‌شود.`}</span></span></div>}</>;
  const t70 = fromAmount && toAmount ? "\u0642\u06CC\u0645\u062A \u0644\u062D\u0638\u0647\u200C\u0627\u06CC" : "\u0642\u06CC\u0645\u062A \u062A\u0642\u0631\u06CC\u0628\u06CC";
  let t71;
  if ($[254] !== t70) {
    t71 = <p className="text-xs text-charcoal-900 md:text-sm">{t70}:</p>;
    $[254] = t70;
    $[255] = t71;
  } else {
    t71 = $[255];
  }
  let t72;
  if ($[256] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <span>1</span>;
    $[256] = t72;
  } else {
    t72 = $[256];
  }
  let t73;
  if ($[257] !== finalMarket) {
    t73 = finalMarket.split("-");
    $[257] = finalMarket;
    $[258] = t73;
  } else {
    t73 = $[258];
  }
  let t74;
  if ($[259] !== t73[0]) {
    t74 = <span className="px-1">{t73[0]}</span>;
    $[259] = t73[0];
    $[260] = t74;
  } else {
    t74 = $[260];
  }
  let t75;
  if ($[261] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <span>=</span>;
    $[261] = t75;
  } else {
    t75 = $[261];
  }
  let t76;
  if ($[262] !== finalMarket || $[263] !== priceRoundDown || $[264] !== shownPrice) {
    t76 = shownPrice ? <span className="px-1">{priceRoundDown(shownPrice, finalMarket.split("-")[1])}</span> : "...";
    $[262] = finalMarket;
    $[263] = priceRoundDown;
    $[264] = shownPrice;
    $[265] = t76;
  } else {
    t76 = $[265];
  }
  let t77;
  if ($[266] !== finalMarket) {
    t77 = finalMarket.split("-");
    $[266] = finalMarket;
    $[267] = t77;
  } else {
    t77 = $[267];
  }
  let t78;
  if ($[268] !== t77[1]) {
    t78 = <span>{t77[1]}</span>;
    $[268] = t77[1];
    $[269] = t78;
  } else {
    t78 = $[269];
  }
  let t79;
  if ($[270] !== t74 || $[271] !== t76 || $[272] !== t78) {
    t79 = <p className="text-xs font-semibold text-charcoal-900 md:text-sm" dir="ltr"><>{t72}{t74}{t75}{t76}{t78}</></p>;
    $[270] = t74;
    $[271] = t76;
    $[272] = t78;
    $[273] = t79;
  } else {
    t79 = $[273];
  }
  let t80;
  if ($[274] !== t71 || $[275] !== t79) {
    t80 = <div className="mt-4 flex items-center justify-start gap-x-1 md:mt-6">{t71}{t79}</div>;
    $[274] = t71;
    $[275] = t79;
    $[276] = t80;
  } else {
    t80 = $[276];
  }
  const t81 = isLogin && isTradeFormInvalid;
  const t82 = isLogin ? "\u0645\u0639\u0627\u0645\u0644\u0647" : "\u0648\u0631\u0648\u062F \u06CC\u0627 \u062B\u0628\u062A\u200C\u0646\u0627\u0645";
  let t83;
  if ($[277] !== handleLoginButton || $[278] !== t81 || $[279] !== t82) {
    t83 = <div className="mt-2 md:mt-4"><Button variant="solid" color="primary" fullWidth={true} size={isMobileView ? "md" : "lg"} onClick={handleLoginButton} disabled={t81}>{t82}</Button></div>;
    $[277] = handleLoginButton;
    $[278] = t81;
    $[279] = t82;
    $[280] = t83;
  } else {
    t83 = $[280];
  }
  let t84;
  if ($[281] !== activeMarkets || $[282] !== getMarketOptionDisplayName || $[283] !== getMarketOptionSymbol || $[284] !== getMarketPrimaryBalance || $[285] !== getMarketSecondaryBalance || $[286] !== handleModalSelect || $[287] !== hasRestrictedCoinSelector || $[288] !== isLogin || $[289] !== isMarketsModalOpen || $[290] !== marketSearchQuery || $[291] !== prices || $[292] !== setIsMarketsModalOpen || $[293] !== setMarketSearchQuery || $[294] !== wallet) {
    t84 = isMarketsModalOpen && <SelectCryptoModal setIsModalOpen={setIsMarketsModalOpen} isModalOpen={isMarketsModalOpen} coinsList={activeMarkets} selectCoin={handleModalSelect} hasMarketData={true} walletData={wallet} prices={prices} isLogin={isLogin} allowedSymbols={hasRestrictedCoinSelector ? QUICK_ORDER_CURRENCIES : undefined} autoFocus={true} searchText={marketSearchQuery} onSearchTextChange={setMarketSearchQuery} getOptionSymbol={getMarketOptionSymbol} getOptionDisplayName={getMarketOptionDisplayName} getPrimaryBalance={getMarketPrimaryBalance} getSecondaryBalance={getMarketSecondaryBalance} maxHeightClassName="max-h-[450px] md:max-h-[450px]" />;
    $[281] = activeMarkets;
    $[282] = getMarketOptionDisplayName;
    $[283] = getMarketOptionSymbol;
    $[284] = getMarketPrimaryBalance;
    $[285] = getMarketSecondaryBalance;
    $[286] = handleModalSelect;
    $[287] = hasRestrictedCoinSelector;
    $[288] = isLogin;
    $[289] = isMarketsModalOpen;
    $[290] = marketSearchQuery;
    $[291] = prices;
    $[292] = setIsMarketsModalOpen;
    $[293] = setMarketSearchQuery;
    $[294] = wallet;
    $[295] = t84;
  } else {
    t84 = $[295];
  }
  let t85;
  if ($[296] !== t35 || $[297] !== t38 || $[298] !== t68 || $[299] !== t69 || $[300] !== t80 || $[301] !== t83 || $[302] !== t84) {
    t85 = <div className={t35}>{t38}{t68}{t69}{t80}{t83}{t84}</div>;
    $[296] = t35;
    $[297] = t38;
    $[298] = t68;
    $[299] = t69;
    $[300] = t80;
    $[301] = t83;
    $[302] = t84;
    $[303] = t85;
  } else {
    t85 = $[303];
  }
  let t86;
  if ($[304] !== handleKeyDown || $[305] !== t34 || $[306] !== t85) {
    t86 = <div onKeyDown={handleKeyDown}>{t34}{t85}</div>;
    $[304] = handleKeyDown;
    $[305] = t34;
    $[306] = t85;
    $[307] = t86;
  } else {
    t86 = $[307];
  }
  return t86;
};
export default TradeBox;
function _temp(allMarkets, market_0) {
  market_0.to_symbols?.forEach(toMarket => {
    if (!allMarkets.some(item_0 => item_0.name === toMarket.name)) {
      allMarkets.push(toMarket);
    }
  });
  return allMarkets;
}
function _temp2(t0) {
  const {
    field
  } = t0;
  return <IInput {...field} containerCustomStyle="inline-flex items-center" />;
}